import { useEffect, useRef } from 'react';

export default function useHighlightedHighlightNavigation({
  highlightedHighlightId,
  highlights,
  setCurrentPage,
  pageRefs,
  containerRef,
}) {
  const lastHighlightedHighlightId = useRef(null);

  useEffect(() => {
    if (!highlightedHighlightId) {
      lastHighlightedHighlightId.current = null;
      return;
    }

    if (lastHighlightedHighlightId.current === highlightedHighlightId) {
      return;
    }

    lastHighlightedHighlightId.current = highlightedHighlightId;

    const highlight = highlights.find((h) => h.id === highlightedHighlightId || h.id?.toString() === String(highlightedHighlightId));
    if (!highlight) {
      console.warn('Highlight not found:', highlightedHighlightId);
      return;
    }

    const pageNum = highlight.page_number;
    if (!pageNum) {
      console.warn('Highlight has no page number:', highlight);
      return;
    }

    setCurrentPage(pageNum);

    let cancelled = false;
    let cleanupTimeout = null;

    const scrollToHighlight = () => {
      if (cancelled) return;

      requestAnimationFrame(() => {
        if (cancelled) return;

        const pageContainer = pageRefs.current[pageNum];
        if (!pageContainer || !containerRef.current) {
          setTimeout(scrollToHighlight, 100);
          return;
        }

        let highlightedSpans = pageContainer.querySelectorAll(`span[data-highlight-id="${highlightedHighlightId}"]`);

        if (!highlightedSpans || highlightedSpans.length === 0) {
          highlightedSpans = document.querySelectorAll(`span[data-highlight-id="${highlightedHighlightId}"]`);
        }

        console.log('🔍 Searching for highlight spans:', {
          highlightId: highlightedHighlightId,
          pageNum,
          foundInPage: pageContainer.querySelectorAll(`span[data-highlight-id="${highlightedHighlightId}"]`).length,
          foundInDocument: document.querySelectorAll(`span[data-highlight-id="${highlightedHighlightId}"]`).length,
          totalFound: highlightedSpans.length
        });

        if (highlightedSpans && highlightedSpans.length > 0) {
          const isMultiline = highlightedSpans.length > 1 ||
            highlightedSpans[0]?.hasAttribute('data-highlight-part');

          let targetSpan = highlightedSpans[0];

          if (isMultiline && highlightedSpans.length > 1) {
            let firstPartSpan = highlightedSpans[0];
            let firstPartNum = Infinity;

            highlightedSpans.forEach((span) => {
              const partAttr = span.getAttribute('data-highlight-part');
              if (partAttr) {
                const partNum = parseInt(partAttr.split('/')[0], 10);
                if (!Number.isNaN(partNum) && partNum < firstPartNum) {
                  firstPartNum = partNum;
                  firstPartSpan = span;
                }
              }
            });

            targetSpan = firstPartSpan;

            console.log('📍 Multiline highlight navigation:', {
              highlightId: highlightedHighlightId,
              totalSpans: highlightedSpans.length,
              firstPartSpan: firstPartSpan.textContent.substring(0, 30),
              firstPartNum,
              targetSpanRect: firstPartSpan.getBoundingClientRect()
            });
          }

          requestAnimationFrame(() => {
            if (cancelled) return;

            if (!targetSpan.parentNode || !document.contains(targetSpan)) {
              console.warn('⚠️ Target span no longer in DOM, retrying...');
              setTimeout(scrollToHighlight, 200);
              return;
            }

            const spanPageElement = targetSpan.closest('[data-page-number]');
            if (spanPageElement) {
              spanPageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }

            setTimeout(() => {
              if (cancelled) return;

              targetSpan.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              });

              setTimeout(() => {
                if (cancelled) return;

                highlightedSpans.forEach((span) => {
                  if (document.contains(span)) {
                    const spanHighlightId = span.getAttribute('data-highlight-id');
                    if (spanHighlightId === String(highlightedHighlightId) || spanHighlightId === highlightedHighlightId) {
                      const isInsideNote = span.closest('[data-note-id]') !== null;
                      if (!isInsideNote) {
                        span.classList.add('highlight-navigating');
                      }
                    }
                  }
                });

                cleanupTimeout = setTimeout(() => {
                  if (cancelled) return;

                  highlightedSpans.forEach((span) => {
                    if (document.contains(span)) {
                      const spanHighlightId = span.getAttribute('data-highlight-id');
                      if (spanHighlightId === String(highlightedHighlightId) || spanHighlightId === highlightedHighlightId) {
                        span.classList.remove('highlight-navigating');
                      }
                    }
                  });
                }, 2500);
              }, 400);
            }, 200);
          });
        } else {
          const retryTimeout = setTimeout(() => {
            if (!cancelled) {
              scrollToHighlight();
            }
          }, 300);

          cleanupTimeout = retryTimeout;
        }
      });
    };

    requestAnimationFrame(() => {
      if (!cancelled) {
        scrollToHighlight();
      }
    });

    return () => {
      cancelled = true;
      if (cleanupTimeout) {
        clearTimeout(cleanupTimeout);
      }
    };
  }, [highlightedHighlightId, highlights, setCurrentPage, pageRefs, containerRef]);
}
